"use client";

import { Button } from "@packages/components/ui/Button";
import Link from "@packages/components/ui/Link";
import { TextArea } from "@packages/components/ui/TextArea";
import { PARAGRAPH } from "@packages/design-system/constants";
import { useEffect, useState } from "react";

type FeedbackFormProps = {
  userName?: string;
  userEmail?: string;
};

export default function FeedbackForm({
  userName = "",
  userEmail = "",
}: FeedbackFormProps) {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [pageTitle, setPageTitle] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
      setPageTitle(document.title);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    await fetch("/__forms.html", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(
        Array.from(formData.entries()).reduce<Record<string, string>>(
          (acc, [key, value]) => {
            acc[key] = typeof value === "string" ? value : String(value);
            return acc;
          },
          {},
        ),
      ).toString(),
    });
    setSubmitted(true);
  };

  return (
    <div className="container w-full py-6">
      {!submitted && !showForm && (
        <p className="text-muted-heading mx-auto text-center font-medium">
          <Button
            type="button"
            className="underline decoration-1 underline-offset-2"
            variant={"grayLink"}
            onClick={() => setShowForm(true)}
          >
            Help us improve this page
          </Button>
        </p>
      )}
      {submitted ? (
        <div className="my-10 text-center">
          <p className={`font-medium ${PARAGRAPH}`}>
            Thank you for your feedback! We appreciate your input and will use
            it to improve our content.
          </p>
          <p className={`${PARAGRAPH}`}>
            If you need help or have a specific question, please{" "}
            <Link underline href="/contact">
              contact our support team.
            </Link>
          </p>
        </div>
      ) : (
        <form
          name="feedback"
          method="POST"
          action="/__forms.html"
          className={showForm ? "mx-auto max-w-3xl" : "hidden"}
          onSubmit={handleSubmit}
        >
          {/* Feedback textarea FIRST */}
          <label
            htmlFor="feedback"
            className="text-heading block text-sm/6 font-medium"
          >
            What could make this page better?
          </label>
          <div className="mt-2">
            <TextArea
              id="feedback"
              name="feedback"
              className="max-w-4xl"
              rows={4}
              defaultValue={""}
              required
              placeholder="Your feedback (e.g., unclear info, missing details, something's broken, etc.)"
            />
          </div>
          {/* Hidden fields follow */}
          <input type="hidden" name="form-name" value="feedback" />
          <input
            type="hidden"
            name="title"
            id="title"
            value={pageTitle}
            readOnly
          />
          <input
            type="hidden"
            name="url"
            id="url"
            value={currentUrl}
            readOnly
          />
          <p className="hidden">
            <input
              type="text"
              name="name"
              id="yourname"
              value={userName}
              readOnly={!!userName}
            />
          </p>
          <p className="hidden">
            <input
              type="email"
              name="email"
              id="youremail"
              value={userEmail}
              readOnly={!!userEmail}
            />
          </p>
          <div className="mt-4 flex justify-end">
            <Button type="submit" variant="outline">
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
